import React, { useState, useEffect,useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import "./EvaluationPathologie.css";
import toast from 'react-hot-toast';
import { saveDossier } from "../../redux/dispatch";
import SaveLoader from "../shared/SaveLoader";
import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";
import useConfidence from "../../hooks/useConfidence";
import ConfidenceLabel from "../shared/ConfidenceLabel";

const RUBRIQUE = "evaluation_pathologie";
const EXTENSIONS = [StarterKit, Underline];

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const buttons = [
    { label: "G",      title: "Gras",          action: () => editor.chain().focus().toggleBold().run(),        active: editor.isActive("bold"),        style: { fontWeight: "bold" } },
    { label: "I",      title: "Italique",       action: () => editor.chain().focus().toggleItalic().run(),      active: editor.isActive("italic"),      style: { fontStyle: "italic" } },
    { label: "S",      title: "Barré",          action: () => editor.chain().focus().toggleStrike().run(),      active: editor.isActive("strike"),      style: { textDecoration: "line-through" } },
    { label: "U",       title: "Souligné",        action: () => editor.chain().focus().toggleUnderline().run(),             active: editor.isActive("underline"),      style: { textDecoration: "underline" } },
    { label: "H1",     title: "Titre 1",        action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(), active: editor.isActive("heading", { level: 1 }) },
    { label: "H2",     title: "Titre 2",        action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), active: editor.isActive("heading", { level: 2 }) },
    { label: "• List", title: "Liste à puces",  action: () => editor.chain().focus().toggleBulletList().run(),  active: editor.isActive("bulletList") },
    { label: "1. List",title: "Liste numérotée",action: () => editor.chain().focus().toggleOrderedList().run(), active: editor.isActive("orderedList") },
    { label: "❝",      title: "Citation",       action: () => editor.chain().focus().toggleBlockquote().run(),  active: editor.isActive("blockquote") },
    { label: "—",      title: "Séparateur",     action: () => editor.chain().focus().setHorizontalRule().run(), active: false },
    { label: "↩",      title: "Annuler",        action: () => editor.chain().focus().undo().run(),              active: false },
    { label: "↪",      title: "Rétablir",       action: () => editor.chain().focus().redo().run(),              active: false },
  ];

  return (
    <div className="ep-menubar">
      {buttons.map((btn, i) => (
        <button
          key={i}
          title={btn.title}
          className={`ep-menubar-btn ${btn.active ? "ep-menubar-btn--active" : ""}`}
          style={btn.style || {}}
          onClick={btn.action}
          type="button"
        >
          {btn.label}
        </button>
      ))}
    </div>
  );
};

const EvaluationPathologie = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();
  const rawImages = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || []);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];
  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;
  const formulairesData  = useSelector((s) => s.formulaires.data);
  const savedData = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.data || "");
const { getConfidence } = useConfidence(id, RUBRIQUE);
  const gc = (field) => getConfidence(field);
const [saving, setSaving] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
const contentRef = useRef(savedData || "");

const editor = useEditor({
  extensions: EXTENSIONS,
  content: savedData || "",
  onUpdate: ({ editor }) => {
    contentRef.current = editor.getHTML();
  },
});


  useEffect(() => {
    if (editor && savedData) {
      editor.commands.setContent(savedData);
    }
  }, [id]);

// APRÈS
const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav(RUBRIQUE);
    const handleSave = () => {
    dispatch(updateFormData(id, RUBRIQUE, "data", contentRef.current));
    };

    useEffect(() => {
      return () => {
        handleSave();
      };
    }, []);

   const handleSaveAll = async () => {
setSaving(true);
     if (!editor) return;
    const html = editor.getHTML();
    dispatch(updateFormData(id, RUBRIQUE, "data", html));
    try {
        const result = await dispatch(saveDossier());
        toast.success(result.message);
        navigate("/");
    } catch (err) {
        toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
    }
    finally {
    setSaving(false);
  }
  };

  const handleCancel = () => {
    if (editor) editor.commands.setContent(savedData || "");
  };

  if (!dossier) {
    return (
      <div className="ep-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="ep-page">
         {saving && <SaveLoader />}
      {/* ── Header ── */}
      <header className="ep-header">
        <div className="ep-header-left">
          <button className="ep-back-btn" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="ep-title">Dossier {dossier.numero_dossier || dossier.id}</h1>
            <p className="ep-subtitle">Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}</p>
          </div>
        </div>
        <button className="ep-save-btn" onClick={handleSaveAll} disabled={saving}>Sauvegarder les modifications</button>
      </header>

      <div className="ep-body">
        {/* ── Colonne gauche : image ── */}
        <aside className="ep-image-panel">
          <div className="ep-image-header">
            <span className="ep-rubrique-label">Évaluation Pathologie</span>
            <span className="ep-image-counter">Image {imgIndex + 1} / {IMAGES.length}</span>
          </div>
          <div className="ep-image-viewer">
            <img src={IMAGES[imgIndex]} alt={`Page ${imgIndex + 1}`} className="ep-image" />
          </div>
          <div className="ep-image-nav">
            <button className="ep-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
            <div className="ep-image-dots">
              {IMAGES.map((_, i) => (
                <span key={i} className={`ep-dot ${i === imgIndex ? "ep-dot--active" : ""}`} onClick={() => setImgIndex(i)} />
              ))}
            </div>
            <button className="ep-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
          </div>
        </aside>

        {/* ── Colonne droite : formulaire ── */}
        <main className="ep-form-panel">
          <div className="ep-form-scroll">

            <div className="ep-description" style={{ borderColor: gc("data").color || undefined }}>
              <h2 className="ep-description-title">Fiche d'évaluation spécifique des pathologies <ConfidenceLabel confidence={gc("data")} /></h2>
             
              <ul className="ep-description-list">
                <li>Leucémies / Lymphomes</li>
                <li>Néphroblastome</li>
                <li>Autres Tumeurs </li>
              </ul>
            </div>

            <div className="ep-editor-wrapper">
              <MenuBar editor={editor} />
              <EditorContent editor={editor} className="ep-editor" />
            </div>

          </div>

          {/* ── Actions ── */}
          {/* <div className="ep-form-actions">
            <button className="ep-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="ep-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}

<RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />        </main>
      </div>
    </div>
  );
};

export default EvaluationPathologie;